/**
 * Каналы (изменение v1.7): создание, подписка, управление.
 *
 * Сервер — источник истины. Ответ операции кладётся в реплику сразу (карточка канала живёт в
 * `Conversation.channel`), а событие `channel.updated` придёт следом и обновит другие устройства.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { api, idempotency, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type {
  Channel,
  ChannelCreate,
  ChannelPatch,
  ChannelSubscriber,
  UsernameAvailability,
} from '@shared/api/types'
import { conversationsRepo, currentUser, useReplicaMaybe } from '@shared/db'
import { useToast } from '@shared/ui'

/**
 * Кладёт ленту канала в реплику: карточку — в уже загруженный диалог, а после создания или
 * подписки саму ленту забирает `GET /conversations/{id}`. Без этого экран канала открывался бы
 * с «Диалог недоступен»: sync-событие приходит позже перехода.
 */
async function syncChannel(channel: Channel, strict = false, expectedOwnerUserId?: string): Promise<boolean> {
  if (expectedOwnerUserId && currentUser() !== expectedOwnerUserId) return false
  const feed = await conversationsRepo.get(channel.conversation_id)
  if (expectedOwnerUserId && currentUser() !== expectedOwnerUserId) return false
  if (feed) {
    await conversationsRepo.patchOptimistic(feed.id, { channel })
    return !expectedOwnerUserId || currentUser() === expectedOwnerUserId
  }
  try {
    if (expectedOwnerUserId && currentUser() !== expectedOwnerUserId) return false
    const fresh = unwrap(
      await api.GET('/conversations/{conversationId}', {
        params: { path: { conversationId: channel.conversation_id } },
      }),
    )
    if (expectedOwnerUserId && currentUser() !== expectedOwnerUserId) return false
    await conversationsRepo.put(fresh)
    return !expectedOwnerUserId || currentUser() === expectedOwnerUserId
  } catch (error) {
    if (strict) throw error
    // Не подписан или лента ещё недоступна — она приедет обычным обменом.
    return true
  }
}

/** Карточка канала с сервера: нужна и для тех, кто ещё не подписан. */
export function useChannel(channelId: string | null): {
  channel: Channel | null
  loading: boolean
  error: ApiError | null
  reload: () => void
} {
  const [channel, setChannel] = useState<Channel | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(channelId !== null)
  const [nonce, setNonce] = useState(0)
  const replica = useReplicaMaybe(
    () => (channel ? conversationsRepo.get(channel.conversation_id) : Promise.resolve(undefined)),
    [channel?.conversation_id],
  )

  useEffect(() => {
    if (!channelId) {
      setChannel(null)
      setLoading(false)
      return
    }
    let alive = true
    setLoading(true)
    void (async () => {
      try {
        const result = unwrap(await api.GET('/channels/{channelId}', { params: { path: { channelId } } }))
        if (!alive) return
        setChannel(result)
        setError(null)
        await syncChannel(result)
      } catch (cause) {
        if (alive) setError(cause instanceof ApiError ? cause : null)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [channelId, nonce])

  const current =
    replica?.channel && channel && replica.channel.version >= channel.version ? replica.channel : channel
  return { channel: current, loading, error, reload: useCallback(() => setNonce((n) => n + 1), []) }
}

/** В группе состав доступен участникам; broadcast подписчики остаются видны администраторам. */
export function useChannelSubscribers(
  channelId: string | null,
  group = false,
): {
  items: ChannelSubscriber[]
  loading: boolean
  error: boolean
  hasMore: boolean
  loadMore: () => void
  reload: () => void
} {
  const [items, setItems] = useState<ChannelSubscriber[]>([])
  const [loading, setLoading] = useState(channelId !== null)
  const [error, setError] = useState(false)
  const [nonce, setNonce] = useState(0)
  const [cursor, setCursor] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  useEffect(() => {
    if (!channelId) return
    let alive = true
    setLoading(true)
    setError(false)
    void (async () => {
      try {
        const params = { path: { channelId }, query: { limit: 40, ...(cursor ? { cursor } : {}) } }
        const result = group
          ? unwrap(await api.GET('/channels/{channelId}/members', { params }))
          : unwrap(await api.GET('/channels/{channelId}/subscribers', { params }))
        if (alive) {
          const rows: ChannelSubscriber[] = result.items.map((item) => ({
            ...item,
            role: item.role === 'member' ? 'subscriber' : item.role,
          }))
          setItems((previous) =>
            cursor ? [...new Map([...previous, ...rows].map((item) => [item.user_id, item])).values()] : rows,
          )
          setNextCursor(result.next_cursor)
        }
      } catch {
        if (alive) {
          setError(true)
          if (!cursor) setItems([])
        }
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [channelId, group, nonce, cursor])
  return {
    items,
    loading,
    error,
    hasMore: nextCursor !== null,
    loadMore: useCallback(() => {
      if (nextCursor) setCursor(nextCursor)
    }, [nextCursor]),
    reload: useCallback(() => {
      setCursor(null)
      setNonce((n) => n + 1)
    }, []),
  }
}

/**
 * Проверка никнейма канала по мере ввода — как у U-15: запрос через паузу, ответ только
 * для последнего значения.
 */
export function useChannelUsernameCheck(
  value: string,
  channelId?: string,
): { state: 'idle' | 'checking' | 'done'; result: UsernameAvailability | null } {
  const [state, setState] = useState<'idle' | 'checking' | 'done'>('idle')
  const [result, setResult] = useState<UsernameAvailability | null>(null)
  const latest = useRef('')

  useEffect(() => {
    const handle = value.trim().replace(/^@/, '')
    latest.current = handle
    if (handle.length < 5) {
      setState('idle')
      setResult(null)
      return
    }
    // Результат относится к предыдущей строке. Пока новая проверяется, он не
    // должен оставлять кнопку «Далее» активной для ещё не проверенного никнейма.
    setState('checking')
    setResult(null)
    const controller = new AbortController()
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const answer = unwrap(
            await api.GET('/channels/username-availability', {
              params: { query: { username: handle, ...(channelId ? { channel_id: channelId } : {}) } },
              signal: controller.signal,
            }),
          )
          if (latest.current !== handle) return
          setResult(answer)
          setState('done')
        } catch {
          if (latest.current === handle && !controller.signal.aborted) {
            setResult(null)
            setState('idle')
          }
        }
      })()
    }, 400)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [value, channelId])

  return { state, result }
}

export interface ChannelActions {
  busy: boolean
  /** Незавершённое создание, восстановленное после reload/unmount. */
  pendingCreate: ChannelCreate | null
  create: (input: ChannelCreate) => Promise<Channel | null>
  /** Отменяет старое намерение, когда пользователь начал менять форму. */
  discardPendingCreate: () => void
  /** Повторяет сохранённый payload с исходным Idempotency-Key. */
  resumeCreate: () => Promise<Channel | null>
  /** Восстановление после reload: ограниченно повторяет неоднозначные ответы с тем же ключом. */
  resumeCreateRecovered: (signal?: AbortSignal) => Promise<Channel | null>
  patch: (channelId: string, patch: ChannelPatch, version?: number) => Promise<Channel | null>
  remove: (channelId: string) => Promise<boolean>
  subscribe: (channelId: string) => Promise<Channel | null>
  unsubscribe: (channelId: string) => Promise<boolean>
  join: (inviteCode: string) => Promise<Channel | null>
  addSubscribers: (channelId: string, userIds: string[]) => Promise<Channel | null>
  removeSubscriber: (channelId: string, userId: string) => Promise<boolean>
  setRole: (channelId: string, userId: string, role: 'admin' | 'subscriber') => Promise<boolean>
  resetInvite: (channelId: string) => Promise<string | null>
}

const MESSAGES: Partial<Record<string, string>> = {
  username_taken: 'Этот никнейм уже занят',
  channel_limit_reached: 'Больше десяти каналов создать нельзя',
  not_channel_admin: 'Нужны права администратора канала',
  invite_link_invalid: 'Ссылка недействительна',
}

interface CreateIntent {
  version: 2
  owner_user_id: string
  fingerprint: string
  key: string
  payload: ChannelCreate
}

interface InFlightCreate {
  owner_user_id: string
  fingerprint: string
  key: string
  promise: Promise<Channel | null>
}

const CREATE_INTENT_STORAGE_KEY = 'dvizh:channel-create-intent:v2'
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const RECOVERED_CREATE_RETRY_MS = [250, 750] as const

function waitForCreateRetry(delayMs: number, signal?: AbortSignal): Promise<boolean> {
  if (signal?.aborted) return Promise.resolve(false)
  return new Promise((resolve) => {
    let settled = false
    const finish = (ready: boolean): void => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
      resolve(ready)
    }
    const onAbort = (): void => finish(false)
    const timer = setTimeout(() => finish(true), delayMs)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Точно то тело, которое уйдёт на сервер и будет повторено после reload.
 * `subscriber_ids` копируется: мутация state экрана не должна изменить
 * уже сохранённое намерение.
 */
function normalizeChannelCreate(input: ChannelCreate): ChannelCreate {
  return {
    title: input.title,
    kind: input.kind,
    ...(input.format === undefined ? {} : { format: input.format }),
    ...(input.description === undefined ? {} : { description: input.description }),
    ...(input.username === undefined ? {} : { username: input.username }),
    ...(input.photo_media_id === undefined ? {} : { photo_media_id: input.photo_media_id }),
    ...(input.comments_enabled === undefined ? {} : { comments_enabled: input.comments_enabled }),
    ...(input.subscriber_ids === undefined ? {} : { subscriber_ids: [...input.subscriber_ids] }),
  }
}

function channelCreateFingerprint(input: ChannelCreate): string {
  return JSON.stringify(normalizeChannelCreate(input))
}

function parseStoredChannelCreate(value: unknown): ChannelCreate | null {
  if (typeof value !== 'object' || value === null) return null
  const payload = value as Partial<ChannelCreate>
  if (typeof payload.title !== 'string' || (payload.kind !== 'public' && payload.kind !== 'private')) {
    return null
  }
  if (payload.format !== undefined && payload.format !== 'channel' && payload.format !== 'group') return null
  if (payload.description !== undefined && typeof payload.description !== 'string') return null
  if (payload.username !== undefined && typeof payload.username !== 'string') return null
  if (payload.photo_media_id !== undefined && typeof payload.photo_media_id !== 'string') return null
  if (payload.comments_enabled !== undefined && typeof payload.comments_enabled !== 'boolean') return null
  if (
    payload.subscriber_ids !== undefined &&
    (!Array.isArray(payload.subscriber_ids) ||
      !payload.subscriber_ids.every((subscriberId) => typeof subscriberId === 'string'))
  ) {
    return null
  }
  return normalizeChannelCreate(payload as ChannelCreate)
}

function createIntentStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

/** Privacy-cleanup удаляет только intent завершившегося владельца. */
export function clearChannelCreateIntentForOwner(ownerUserId: string): void {
  const storage = createIntentStorage()
  if (!storage) return
  try {
    const raw = storage.getItem(CREATE_INTENT_STORAGE_KEY)
    if (!raw) return
    const value = JSON.parse(raw) as { owner_user_id?: unknown }
    if (value.owner_user_id === ownerUserId) storage.removeItem(CREATE_INTENT_STORAGE_KEY)
  } catch {
    // Повреждённый payload не содержит безопасно проверяемого owner.
    storage.removeItem(CREATE_INTENT_STORAGE_KEY)
  }
}

function parseStoredCreateIntent(raw: string): CreateIntent | null {
  try {
    const value: unknown = JSON.parse(raw)
    const payload =
      typeof value === 'object' && value !== null
        ? parseStoredChannelCreate((value as Partial<CreateIntent>).payload)
        : null
    if (
      typeof value === 'object' &&
      value !== null &&
      (value as Partial<CreateIntent>).version === 2 &&
      typeof (value as Partial<CreateIntent>).owner_user_id === 'string' &&
      (value as CreateIntent).owner_user_id.length > 0 &&
      typeof (value as Partial<CreateIntent>).fingerprint === 'string' &&
      typeof (value as Partial<CreateIntent>).key === 'string' &&
      UUID_V4.test((value as CreateIntent).key) &&
      payload !== null &&
      channelCreateFingerprint(payload) === (value as CreateIntent).fingerprint
    ) {
      return { ...(value as CreateIntent), payload }
    }
  } catch {
    return null
  }
  return null
}

function readStoredCreateIntent(ownerUserId: string | null): CreateIntent | null {
  const storage = createIntentStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(CREATE_INTENT_STORAGE_KEY)
    if (!raw) return null
    const intent = parseStoredCreateIntent(raw)
    if (!intent) {
      storage.removeItem(CREATE_INTENT_STORAGE_KEY)
      return null
    }
    // `currentUser()` кратковременно равен null во время восстановления сессии.
    // Это пауза, а не logout: сохраняем intent до появления владельца.
    if (!ownerUserId) return null
    if (intent.owner_user_id === ownerUserId) return intent

    // Intent от прежнего аккаунта нельзя выполнять в текущей сессии.
    storage.removeItem(CREATE_INTENT_STORAGE_KEY)
  } catch {
    try {
      storage.removeItem(CREATE_INTENT_STORAGE_KEY)
    } catch {
      // sessionStorage может быть запрещён политикой браузера.
    }
  }
  return null
}

function storeCreateIntent(intent: CreateIntent): void {
  try {
    createIntentStorage()?.setItem(CREATE_INTENT_STORAGE_KEY, JSON.stringify(intent))
  } catch {
    // В режимах без sessionStorage ключ всё равно живёт в ref до unmount.
  }
}

function removeStoredCreateIntent(intent: CreateIntent): void {
  const storage = createIntentStorage()
  if (!storage) return
  try {
    const raw = storage.getItem(CREATE_INTENT_STORAGE_KEY)
    const stored = raw ? parseStoredCreateIntent(raw) : null
    if (
      stored?.owner_user_id === intent.owner_user_id &&
      stored.fingerprint === intent.fingerprint &&
      stored.key === intent.key
    ) {
      storage.removeItem(CREATE_INTENT_STORAGE_KEY)
    }
  } catch {
    // Недоступное хранилище не должно ломать создание канала.
  }
}

function isTerminalCreateError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.code === 'idempotency_in_progress') return false
  // После истечения сессии сохраняем форму и ключ для повторного входа тем же
  // аккаунтом. Intent привязан к owner_user_id, поэтому другой аккаунт его не выполнит.
  if (error.status === 401 || error.status === 408 || error.status === 425 || error.status === 429) {
    return false
  }
  return error.status >= 400 && error.status < 500
}

export function useChannelActions(): ChannelActions {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const createIntent = useRef<CreateIntent | null>(null)
  const inFlightCreate = useRef<InFlightCreate | null>(null)
  const [pendingCreate, setPendingCreate] = useState<ChannelCreate | null>(() => {
    const stored = readStoredCreateIntent(currentUser())
    createIntent.current = stored
    return stored?.payload ?? null
  })

  const run = useCallback(
    async <T>(action: () => Promise<T>, fallback: string): Promise<T | null> => {
      setBusy(true)
      try {
        return await action()
      } catch (error) {
        const code = error instanceof ApiError ? error.code : ''
        toast.show(MESSAGES[code] ?? fallback, { tone: 'danger' })
        return null
      } finally {
        setBusy(false)
      }
    },
    [toast],
  )

  const executeCreateIntent = useCallback(
    (intent: CreateIntent) => {
      if (currentUser() !== intent.owner_user_id) {
        if (createIntent.current?.key === intent.key) {
          createIntent.current = null
          setPendingCreate(null)
        }
        removeStoredCreateIntent(intent)
        return Promise.resolve(null)
      }

      const active = inFlightCreate.current
      if (
        active?.owner_user_id === intent.owner_user_id &&
        active.fingerprint === intent.fingerprint &&
        active.key === intent.key
      ) {
        return active.promise
      }

      const request = run(async () => {
        let channel: Channel
        try {
          channel = unwrap(await api.POST('/channels', { body: intent.payload, ...idempotency(intent.key) }))
        } catch (error) {
          // Только однозначный 4xx завершает намерение. 408/425/429, 5xx и обрыв
          // сети могут скрывать уже записанный ответ, поэтому сохраняют ключ.
          if (isTerminalCreateError(error)) {
            if (createIntent.current?.key === intent.key) {
              createIntent.current = null
              setPendingCreate(null)
            }
            removeStoredCreateIntent(intent)
          }
          throw error
        }

        // Для нового канала реплика должна быть записана до перехода. Ошибка GET/IndexedDB
        // оставляет намерение для безопасного повтора с тем же ключом.
        const synced = await syncChannel(channel, true, intent.owner_user_id)
        if (!synced) {
          if (createIntent.current?.key === intent.key) {
            createIntent.current = null
            setPendingCreate(null)
          }
          removeStoredCreateIntent(intent)
          return null
        }
        if (createIntent.current?.key === intent.key) {
          createIntent.current = null
          setPendingCreate(null)
        }
        removeStoredCreateIntent(intent)
        return channel
      }, 'Не удалось создать канал')
      const promise = request.finally(() => {
        if (inFlightCreate.current?.promise === promise) inFlightCreate.current = null
      })
      inFlightCreate.current = {
        owner_user_id: intent.owner_user_id,
        fingerprint: intent.fingerprint,
        key: intent.key,
        promise,
      }
      return promise
    },
    [run],
  )

  const create = useCallback(
    (input: ChannelCreate) => {
      // После timeout/network error сервер мог уже создать канал. Повтор того же
      // намерения обязан нести тот же Idempotency-Key и тот же нормализованный payload.
      const ownerUserId = currentUser()
      if (!ownerUserId) {
        // Не стираем crash-safe intent в переходный момент, когда auth ещё
        // не восстановил owner. Без owner новый POST всё равно запрещён.
        return Promise.resolve(null)
      }
      const payload = normalizeChannelCreate(input)
      const fingerprint = channelCreateFingerprint(payload)
      const previous = createIntent.current
      if (previous && previous.owner_user_id !== ownerUserId) {
        removeStoredCreateIntent(previous)
        createIntent.current = null
      }
      const stored = createIntent.current ?? readStoredCreateIntent(ownerUserId)
      if (stored && stored.fingerprint !== fingerprint) removeStoredCreateIntent(stored)
      const intent: CreateIntent =
        stored?.fingerprint === fingerprint
          ? stored
          : { version: 2, owner_user_id: ownerUserId, fingerprint, key: uuid(), payload }
      createIntent.current = intent
      setPendingCreate(intent.payload)
      storeCreateIntent(intent)
      return executeCreateIntent(intent)
    },
    [executeCreateIntent],
  )

  const resumeCreate = useCallback((): Promise<Channel | null> => {
    const ownerUserId = currentUser()
    if (!ownerUserId) return Promise.resolve(null)

    const previous = createIntent.current
    if (previous && previous.owner_user_id !== ownerUserId) {
      if (previous) removeStoredCreateIntent(previous)
      createIntent.current = null
      setPendingCreate(null)
    }
    const intent = createIntent.current ?? readStoredCreateIntent(ownerUserId)
    if (!intent) return Promise.resolve(null)
    createIntent.current = intent
    setPendingCreate(intent.payload)
    return executeCreateIntent(intent)
  }, [executeCreateIntent])

  const discardPendingCreate = useCallback((): void => {
    // Это явное действие из формы. В отличие от временного `owner === null`,
    // реальная правка означает, что старый payload больше нельзя auto-resume.
    createIntent.current = null
    setPendingCreate(null)
    try {
      createIntentStorage()?.removeItem(CREATE_INTENT_STORAGE_KEY)
    } catch {
      // sessionStorage может быть запрещён политикой браузера.
    }
  }, [])

  const resumeCreateRecovered = useCallback(
    async (signal?: AbortSignal): Promise<Channel | null> => {
      for (let attempt = 0; attempt <= RECOVERED_CREATE_RETRY_MS.length; attempt += 1) {
        if (signal?.aborted) return null
        const channel = await resumeCreate()
        if (channel || signal?.aborted || createIntent.current === null) return channel

        const delay = RECOVERED_CREATE_RETRY_MS[attempt]
        if (delay === undefined || !(await waitForCreateRetry(delay, signal))) return null
      }
      return null
    },
    [resumeCreate],
  )

  const patch = useCallback(
    (channelId: string, body: ChannelPatch, version?: number) =>
      run(async () => {
        const channel = unwrap(
          await api.PATCH('/channels/{channelId}', {
            params: { path: { channelId } },
            ...(version === undefined ? {} : { headers: { 'If-Match': `"${version}"` } }),
            body,
          }),
        )
        await syncChannel(channel)
        return channel
      }, 'Не удалось сохранить изменения'),
    [run],
  )

  const remove = useCallback(
    async (channelId: string) =>
      (await run(async () => {
        // 204 без тела — `unwrap` неприменим (ошибку бросает middleware клиента).
        await api.DELETE('/channels/{channelId}', { params: { path: { channelId } } })
        return true
      }, 'Не удалось удалить канал')) ?? false,
    [run],
  )

  const subscribe = useCallback(
    (channelId: string) =>
      run(async () => {
        const channel = unwrap(
          await api.PUT('/channels/{channelId}/subscription', { params: { path: { channelId } } }),
        )
        await syncChannel(channel)
        return channel
      }, 'Не удалось подписаться'),
    [run],
  )

  const unsubscribe = useCallback(
    async (channelId: string) =>
      (await run(async () => {
        await api.DELETE('/channels/{channelId}/subscription', { params: { path: { channelId } } })
        return true
      }, 'Не удалось отписаться')) ?? false,
    [run],
  )

  const join = useCallback(
    (inviteCode: string) =>
      run(async () => {
        const channel = unwrap(await api.POST('/channels/join', { body: { invite_code: inviteCode } }))
        if (channel.join_status !== 'pending') await syncChannel(channel)
        return channel
      }, 'Не удалось вступить в канал'),
    [run],
  )

  const addSubscribers = useCallback(
    (channelId: string, userIds: string[]) =>
      run(async () => {
        const channel = unwrap(
          await api.POST('/channels/{channelId}/subscribers', {
            params: { path: { channelId } },
            body: { user_ids: userIds },
          }),
        )
        await syncChannel(channel)
        return channel
      }, 'Не удалось добавить подписчиков'),
    [run],
  )

  const removeSubscriber = useCallback(
    async (channelId: string, userId: string) =>
      (await run(async () => {
        await api.DELETE('/channels/{channelId}/subscribers/{userId}', {
          params: { path: { channelId, userId } },
        })
        return true
      }, 'Не удалось удалить подписчика')) ?? false,
    [run],
  )

  const setRole = useCallback(
    async (channelId: string, userId: string, role: 'admin' | 'subscriber') =>
      (await run(async () => {
        unwrap(
          await api.PUT('/channels/{channelId}/subscribers/{userId}', {
            params: { path: { channelId, userId } },
            body: { role },
          }),
        )
        return true
      }, 'Не удалось изменить роль')) ?? false,
    [run],
  )

  const resetInvite = useCallback(
    (channelId: string) =>
      run(async () => {
        const result = unwrap(
          await api.POST('/channels/{channelId}/invite-link', { params: { path: { channelId } } }),
        )
        return result.invite_code
      }, 'Не удалось обновить ссылку'),
    [run],
  )

  return {
    busy,
    pendingCreate,
    create,
    discardPendingCreate,
    resumeCreate,
    resumeCreateRecovered,
    patch,
    remove,
    subscribe,
    unsubscribe,
    join,
    addSubscribers,
    removeSubscriber,
    setRole,
    resetInvite,
  }
}
